import { BoundaryApprovalJob } from './BoundaryApprovalJob';

/**
 * Manages mission activation and state for the Boundary Approval task
 */
export class BoundaryApprovalMissionManager {
  private jobs: Map<string, BoundaryApprovalJob> = new Map();
  private activeMissionId: string | null = null;
  private onStateChangeCallback?: () => void;

  /**
   * Sets the list of jobs to manage
   */
  setJobs(jobs: BoundaryApprovalJob[]): void {
    this.jobs.clear();
    jobs.forEach((job) => this.jobs.set(job.id, job));
  }

  /**
   * Gets all jobs
   */
  getJobs(): BoundaryApprovalJob[] {
    return Array.from(this.jobs.values());
  }

  /**
   * Gets a specific job by ID
   */
  getJob(jobId: string): BoundaryApprovalJob | undefined {
    return this.jobs.get(jobId);
  }

  /**
   * Gets the currently active job
   */
  getActiveJob(): BoundaryApprovalJob | undefined {
    if (!this.activeMissionId) {
      return undefined;
    }

    return this.jobs.get(this.activeMissionId);
  }

  /**
   * Activates a job/mission
   * @param jobId The job ID to activate
   * @returns Object with success status and optional message
   */
  async activateJob(jobId: string): Promise<{ success: boolean; message?: string; needsConfirmation?: boolean }> {
    const job = this.jobs.get(jobId);

    if (!job) {
      return { success: false, message: 'Job not found' };
    }

    if (!job.canBeActivated()) {
      return { success: false, message: 'This job has already been approved' };
    }

    // Check if there's already an active job with unsaved edits
    const currentActiveJob = this.getActiveJob();

    if (currentActiveJob && currentActiveJob.id !== jobId) {
      if (currentActiveJob.hasUnsavedEdits) {
        return {
          success: false,
          message: 'You have unsaved changes. Do you really want to activate another mission?',
          needsConfirmation: true,
        };
      }

      // Deactivate the current job
      currentActiveJob.setActive(false);
    }

    // Activate the new job
    job.setActive(true);
    this.activeMissionId = jobId;

    this.notifyStateChange();

    return { success: true };
  }

  /**
   * Deactivates the current job
   */
  deactivateCurrentJob(): void {
    const currentJob = this.getActiveJob();

    if (currentJob) {
      currentJob.setActive(false);
      currentJob.resetEdits();
      this.activeMissionId = null;
      this.notifyStateChange();
    }
  }

  /**
   * Marks the current active job as having unsaved edits
   */
  markCurrentJobAsEdited(): void {
    const activeJob = this.getActiveJob();

    if (activeJob) {
      activeJob.setHasUnsavedEdits(true);
      this.notifyStateChange();
    }
  }

  /**
   * Checks if the current active job has unsaved edits
   */
  hasUnsavedEdits(): boolean {
    const activeJob = this.getActiveJob();

    return activeJob?.hasUnsavedEdits || false;
  }

  /**
   * Marks the current job as approved
   */
  markCurrentJobAsApproved(): void {
    const activeJob = this.getActiveJob();

    if (activeJob) {
      activeJob.markAsApproved();
      this.notifyStateChange();
    }
  }

  /**
   * Toggles job visibility
   */
  toggleJobVisibility(jobId: string): void {
    const job = this.jobs.get(jobId);

    if (job) {
      job.setVisible(!job.isVisible);
      this.notifyStateChange();
    }
  }

  /**
   * Sets a callback to be called when state changes
   */
  onStateChange(callback: () => void): void {
    this.onStateChangeCallback = callback;
  }

  /**
   * Notifies listeners of state change
   */
  private notifyStateChange(): void {
    if (this.onStateChangeCallback) {
      this.onStateChangeCallback();
    }
  }

  /**
   * Resets all state
   */
  reset(): void {
    this.jobs.clear();
    this.activeMissionId = null;
    this.notifyStateChange();
  }
}
